import type { ColumnDef } from '@tanstack/react-table';
import { Plus, Wrench } from 'lucide-react';
import { useDeferredValue, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/ui/data-table';
import { EmptyState } from '@/components/ui/empty-state';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { VesselSelect } from '@/components/ui/vessel-select';
import { useEquipmentTypes } from '@/features/equipment-types/queries';
import { useEquipment } from '@/features/equipment/queries';
import {
  criticalityColor,
  type Equipment,
} from '@/features/equipment/types';
import { useVessels } from '@/features/vessels/queries';
import { vesselDisplayName } from '@/features/vessels/types';
import { ApiError } from '@/lib/api';
import { matchesQuery } from '@/lib/list-search';
import { cn } from '@/lib/utils';

function ListSkeleton() {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          {Array.from({ length: 7 }).map((_, i) => (
            <TableHead key={i}>
              <Skeleton className="h-3 w-20" />
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {Array.from({ length: 5 }).map((_, r) => (
          <TableRow key={r}>
            {Array.from({ length: 7 }).map((_, c) => (
              <TableCell key={c}>
                <Skeleton className="h-4 w-full" />
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export function EquipmentsListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const vesselId = searchParams.get('vesselId');

  const { data, isLoading, isError, error, refetch, isFetching } = useEquipment(
    { vesselId: vesselId ?? undefined },
  );

  const [search, setSearch] = useState('');
  const [criticalityFilter, setCriticalityFilter] = useState<string>('');
  // Default to true — the seed (and likely production) includes
  // inactive equipment (retired generators, decommissioned pumps)
  // and the chief's mental model expects them visible. Unchecking
  // the box hides them.
  const [showInactive, setShowInactive] = useState(true);
  const deferredSearch = useDeferredValue(search);

  // Resolve foreign keys to display names client-side. Both lists are
  // already cached when the user lands here from the sidebar so this
  // is essentially free; on cold load we fall back to the bare id.
  const { data: vessels } = useVessels();
  const { data: types } = useEquipmentTypes();

  const vesselNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const v of vessels ?? []) m.set(v.id, vesselDisplayName(v));
    return m;
  }, [vessels]);

  const typeNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const t of types ?? []) m.set(t.id, t.name);
    return m;
  }, [types]);

  const columns = useMemo<ColumnDef<Equipment>[]>(
    () => [
      {
        accessorKey: 'tag',
        header: () => t('equipment.columns.tag'),
        cell: ({ row }) => (
          <Link
            to={`/equipment/${row.original.id}`}
            onClick={(e) => e.stopPropagation()}
            className="font-mono text-sm text-primary hover:underline"
          >
            {row.original.tag}
          </Link>
        ),
      },
      {
        accessorKey: 'name',
        header: () => t('equipment.columns.name'),
        cell: ({ row }) => (
          <span className="font-medium">{row.original.name}</span>
        ),
      },
      {
        id: 'type',
        header: () => t('equipment.columns.type'),
        cell: ({ row }) =>
          typeNameById.get(row.original.equipmentTypeId) ?? '—',
      },
      {
        id: 'vessel',
        header: () => t('equipment.columns.vessel'),
        cell: ({ row }) => vesselNameById.get(row.original.vesselId) ?? '—',
      },
      {
        accessorKey: 'location',
        header: () => t('equipment.columns.location'),
        cell: ({ getValue }) => getValue<string | null>() ?? '—',
      },
      {
        accessorKey: 'criticality',
        header: () => t('equipment.columns.criticality'),
        cell: ({ getValue }) => {
          const level = getValue<number>();
          return (
            <span
              className={cn(
                'inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium',
                criticalityColor(level),
              )}
            >
              {level}
            </span>
          );
        },
      },
      {
        accessorKey: 'isActive',
        header: () => t('equipment.columns.status'),
        enableSorting: false,
        cell: ({ getValue }) => (
          <span
            className={cn(
              'inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium',
              getValue<boolean>()
                ? 'bg-emerald-100 text-emerald-700'
                : 'bg-muted text-muted-foreground',
            )}
          >
            {t(
              `equipment.status.${getValue<boolean>() ? 'active' : 'inactive'}`,
            )}
          </span>
        ),
      },
    ],
    [t, vesselNameById, typeNameById],
  );

  const errorMessage = (() => {
    if (!isError) return null;
    if (error instanceof ApiError) {
      return t(error.key, {
        defaultValue: t('equipment.list.loadError'),
        ...(error.params ?? {}),
      });
    }
    return t('equipment.list.loadError');
  })();

  const filtered = useMemo(() => {
    const list = data ?? [];
    return list
      .filter((e) => (showInactive ? true : e.isActive))
      .filter((e) =>
        criticalityFilter === ''
          ? true
          : String(e.criticality) === criticalityFilter,
      )
      .filter((e) =>
        matchesQuery(e, deferredSearch, [
          (x) => x.tag,
          (x) => x.name,
          (x) => x.location,
          (x) => x.serialNumber,
          (x) => typeNameById.get(x.equipmentTypeId),
          (x) => vesselNameById.get(x.vesselId),
        ]),
      );
  }, [data, deferredSearch, showInactive, criticalityFilter, typeNameById, vesselNameById]);

  const onVesselChange = (next: string | null) => {
    if (next) {
      searchParams.set('vesselId', next);
    } else {
      searchParams.delete('vesselId');
    }
    setSearchParams(searchParams, { replace: true });
  };

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold">{t('equipment.list.title')}</h1>
        <div className="flex flex-wrap items-center gap-2">
          <Input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('equipment.list.searchPlaceholder')}
            aria-label={t('common.search')}
            className="w-64"
            data-testid="equipment-search"
          />
          <label className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">
              {t('equipment.list.filterByVessel')}
            </span>
            <VesselSelect
              value={vesselId}
              onChange={onVesselChange}
              className="w-56"
            />
          </label>
          <select
            value={criticalityFilter}
            onChange={(e) => setCriticalityFilter(e.target.value)}
            className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
            aria-label={t('equipment.list.filterByCriticality')}
            data-testid="equipment-criticality-filter"
          >
            <option value="">{t('equipment.list.allCriticalities')}</option>
            {[1, 2, 3, 4, 5].map((n) => (
              <option key={n} value={n}>
                {t('equipment.list.criticalityValue', { level: n })}
              </option>
            ))}
          </select>
          <label className="flex items-center gap-1 text-sm text-muted-foreground">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              data-testid="equipment-show-inactive"
            />
            {t('common.showInactive')}
          </label>
          <Button asChild>
            <Link
              to={
                vesselId
                  ? `/equipment/new?vesselId=${vesselId}`
                  : '/equipment/new'
              }
            >
              <Plus aria-hidden />
              {t('equipment.list.newButton')}
            </Link>
          </Button>
        </div>
      </header>

      {errorMessage && (
        <Alert variant="destructive">
          <AlertTitle>{t('equipment.list.loadError')}</AlertTitle>
          <AlertDescription className="space-y-2">
            <p>{errorMessage}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              disabled={isFetching}
            >
              {t('common.retry')}
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {!isError && (
        <DataTable
          columns={columns}
          data={filtered}
          isLoading={isLoading}
          loadingState={<ListSkeleton />}
          emptyState={
            <EmptyState
              icon={Wrench}
              title={t('equipment.list.empty')}
              description={t('equipment.list.emptyHint')}
              action={
                <Button asChild size="sm">
                  <Link
                    to={
                      vesselId
                        ? `/equipment/new?vesselId=${vesselId}`
                        : '/equipment/new'
                    }
                  >
                    <Plus aria-hidden />
                    {t('equipment.list.newButton')}
                  </Link>
                </Button>
              }
            />
          }
          onRowClick={(e) => navigate(`/equipment/${e.id}`)}
        />
      )}
    </div>
  );
}
